import {
  GoogleMap,
  LoadScript,
  Marker,
  useJsApiLoader,
} from "@react-google-maps/api";
import InfoItem from "components/common/detailsPage/InfoItem";
import HorizontalScroller from "components/common/HorizontalScroller";
import DetailsShimmer from "components/common/shimmer/DetailsShimmer";
import { StatusBadge } from "components/common/StatusBadge";
import DataTable from "components/common/table/DataTable";
import { usePermission } from "hooks/usePermission";
import { useQueryParam } from "hooks/useQueryParam";
import React, { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { getBranchDetails } from "store/slices/branchesSlice";
import { getUserDetails } from "store/slices/usersSlice";
import { formatUTCDate } from "utils/utils";
import { Image } from "primereact/image";
import TableShimmer from "components/common/TableShimmer";
import { Link } from "react-router-dom";
import { Copy, SquareArrowUpRightIcon } from "lucide-react";
import { showAlert } from "store/slices/alertSlice";
import PageHeader from "components/common/PageHeader";

function BranchDetails() {
  const {
    t,
    i18n: { language },
  } = useTranslation();
  const {
    loading,
    isLoading,
    itemDetails: branchDetails,
  } = useSelector((state) => state.Branches);
  const { itemDetails: userDetails } = useSelector((state) => state.Users);

  const dispatch = useDispatch();
  const id = useQueryParam("id");
  const { canAction } = usePermission();
  const { canView } = canAction("branches");

  const containerStyle = {
    width: "100%",
    height: "500px",
  };

  const [mapPosition, setMapPosition] = useState(null);
  const libraries = ["places"];

  useEffect(() => {
    if (branchDetails?.latitude && branchDetails?.longitude) {
      const lat = parseFloat(branchDetails.latitude);
      const lng = parseFloat(branchDetails.longitude);

      setMapPosition({ lat, lng });
    }
  }, [branchDetails]);
  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      dispatch(
        showAlert({ text: t("Google map link copied to clipboard!"), type: "success" }),
      );
    } catch (err) {
      dispatch(showAlert({ text: t("Failed to copy"), type: "error" }));
    }
  };

  useEffect(() => {
    if (branchDetails?.updatedBy) {
      dispatch(getUserDetails(branchDetails?.updatedBy));
    }
  }, [branchDetails, dispatch]);

  useEffect(() => {
    if (id && canView) {
      dispatch(getBranchDetails(id));
    }
  }, [id, dispatch, canView]);

  const branchBasicInformationFields = [
    {
      label: t("Id"),
      render : <HorizontalScroller>{branchDetails?._id || "-"}</HorizontalScroller>,
      col: { lg: 6, xs: 6 },
    },
    {
      label: t("Name"),
      value:
        language === "en"
          ? branchDetails?.name?.en
          : branchDetails?.name?.ar || "-",
      col: { lg: 6, xs: 6 },
    },
    { isSpacer: true },
    {
      label: t("Store No."),
      value: branchDetails?.storeNo || "-",
      col: { lg: 6, xs: 6 },
    },
    {
      label: t("Status"),
      render: <StatusBadge status={branchDetails?.status} t={t} />,
      col: { lg: 6, xs: 6 },
    },
    { isSpacer: true },

    {
      label: t("Whatsapp Number"),
      value: branchDetails?.whatsappNumber || "-",
      col: { lg: 6, xs: 6 },
    },
    {
      label: t("Created At"),
      value: formatUTCDate(branchDetails?.createdAt) || "-",
      col: { lg: 6, xs: 6 },
    },
    { isSpacer: true },
    {
      label: t("Updated At"),
      value: formatUTCDate(branchDetails?.updatedAt) || "-",
      col: { lg: 6, xs: 6 },
    },
    {
      label: t("Updated By"),
      value: userDetails?.name || "-",
      col: { lg: 6, xs: 6 },
    },
    { isSpacer: true },
    {
      label: t("Google Map Link"),
      render: (
        <div>
          {branchDetails?.googleMapLink?.trim() ? (
            <div className="d-flex align-items-center gap-2 google_map_link_wrapper">
              <Link
                title={t("Go to google map")}
                to={branchDetails?.googleMapLink}
                target="_blank"
                className="d-flex align-items-center gap-2"
              >
                <span>{branchDetails?.googleMapLink}</span>
                <SquareArrowUpRightIcon width={24} height={24} color="#53c3eb" />
              </Link>
             
            </div>
          ) : (
            "-"
          )}
        </div>
      ),
      col: { lg: 12 },
    },
    { isSpacer: true },
    {
      label: t("Images"),
      col: { lg: 12 },
      render: (
        <div className="branch_images">
          <HorizontalScroller className="d-flex">
            {branchDetails?.imageUrls?.length ? (
              branchDetails.imageUrls.map((url, index) => (
                <Image
                  src={url}
                  alt={`branch-${index}`}
                  className="branch_image"
                  width={50}
                  height={50}
                  preview
                />
              ))
            ) : (
              <span>-</span>
            )}
          </HorizontalScroller>
        </div>
      ),
    },
  ];
  const columns = useMemo(
    () => [
      {
        header: t("Day"),
        key: "day",
      },
      {
        header: t("Open Time"),
        key: "openTime",
      },
      {
        header: t("Close Time"),
        key: "closeTime",
      },
      {
        header: t("Opening Status"),
        key: "isClosed",
        render: (value, item) => (value ? t("Closed") : t("Open") || "-"),
      },
    ],
    [t],
  );
  const branchTrafficDetailsInformation = [
    {
      label: t("Traffic Percentage"),
      value: branchDetails?.trafficPercentage || "-",
      col: { lg: 4, xs: 6 },
    },
    {
      label: t("Waiting Time in Minutes"),
      value: branchDetails?.waitTimeInMinutes || "-",
      col: { lg: 4, xs: 6 },
    },
    {
      label: t("Traffic Status"),
      value: branchDetails?.trafficStatus || "-",
      col: { lg: 4, xs: 6 },
    },
  ];
  const branchRatingAndReviewsInformation = [
    {
      label: t("Average Rating"),
      value: branchDetails?.rating?.avgRating || "-",
      col: { lg: 6, xs: 6 },
    },
    {
      label: t("Total Reviews"),
      value: branchDetails?.rating?.totalReviews || "-",
      col: { lg: 6, xs: 6 },
    },
  ];
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAP_KEY,
    libraries: ["places"],
  });
  const shimmerLayout = [
    {
      col: 6,
      blocks: [
        { rows: 4, cols: 2 },
        { rows: 1, cols: 1 },
      ],
    },
    {
      col: 6,
      type: "map",
      blocks: [{ rows: 1, cols: 1 }],
    },
    {
      col: 6,
      blocks: [{ rows: 2, cols: 1 }],
    },
    {
      col: 6,
      blocks: [
        { rows: 1, cols: 2 },
        { rows: 1, cols: 1 },
        { rows: 1, cols: 2 },
      ],
    },
  ];

  if (loading) {
    return (
      <div className="page-content">
        <PageHeader
          title={t("Store Details")}
          isBackBtn
          className="details_page_title"
        />
        <DetailsShimmer layout={shimmerLayout} />
      </div>
    );
  }

  return (
    <div className="page-content common_details_page branch_details">
      <PageHeader
        title={t("Store Details")}
        isBackBtn
        className="details_page_title"
      />
      <Row>
        <Col lg={6} className="ps-0">
          <div className="common_card" style={{ height: "calc(100% - 20px)" }}>
            <Row>
              <Col lg={12} className="mb-3">
                <h2 className="title_20">{t("Basic Details")}</h2>
              </Col>

              {branchBasicInformationFields.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
        {isLoaded && (
          <Col lg={6}>
            <div
              className="common_card"
              style={{ height: "calc(100% - 20px)" }}
            >
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapPosition || { lat: 0, lng: 0 }}
                zoom={mapPosition ? 15 : 2}
              >
                {mapPosition && <Marker position={mapPosition} />}
              </GoogleMap>
              {/* </LoadScript> */}
            </div>
          </Col>
        )}
        <Col lg={6} className="ps-0">
          <div className="common_card" style={{ height: "calc(100% - 20px)" }}>
            <Row>
              <Col lg={12} className="mb-3">
                <h2 className="title_20">{t("Opening Hours")}</h2>
              </Col>
              {loading && <TableShimmer rows={5} columns={4} />}

              {!loading && branchDetails?.openingHours?.length > 0 && (
                <>
                  <DataTable
                    className="w-100"
                    columns={columns}
                    data={branchDetails?.openingHours}
                    t={t}
                  />
                </>
              )}
            </Row>
          </div>
        </Col>
        <Col lg={6} className="">
          <div className="common_card" style={{ height: "calc(100% - 20px)" }}>
            <Row>
              <Col lg={12} className="mb-3">
                <h2 className="title_20">{t("Traffic & Crowd Status")}</h2>
              </Col>
              {branchTrafficDetailsInformation.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
              <Col lg={12} className="mt-3 mb-3">
                <h2 className="title_20">{t("Rating & Reviews")}</h2>
              </Col>

              {branchRatingAndReviewsInformation.map((item, index) => {
                if (item.isSpacer) {
                  return <div key={index} className="mt-4 w-100" />;
                }

                return (
                  <InfoItem
                    key={index}
                    label={item.label}
                    value={item.value}
                    col={item.col}
                  >
                    {item.render}
                  </InfoItem>
                );
              })}
            </Row>
          </div>
        </Col>
      </Row>
    </div>
  );
}

export default BranchDetails;
